import { computed, ref } from 'vue';

import { BrandResource } from '@/clients/unifiedApi';
import { STATUSES } from '@/constants';
import { Campaign } from '@/models/campaign';
import { Deal } from '@/models/deal';
import { Product } from '@/models/product';
import {
  ActivityFeed,
  BrandMenu,
  BrandPreviewProduct,
  FavoritesItem,
  FavoritesList,
  PublicPromoCode,
  PurchaseHistoryItem,
  ShopBrand,
  StoreFavoritesItem,
} from '@/models/shop';
import { SponsorshipWithSegmentTrackingData } from '@/models/sponsorship';

export const setupShopState = () => {
  const brandActivities = ref<{
    results: ActivityFeed[];
    count: number;
    nextPage: number;
  }>({
    count: 0,
    nextPage: 1,
    results: [],
  });
  const activeCampaign = ref<Campaign>();
  const brandMenus = ref<BrandMenu[]>([]);
  const brands = ref<ShopBrand[]>([]);
  const newBrands = ref<BrandResource[]>([]);
  const deals = ref<Deal[]>([]);
  const favoritesItems = ref<FavoritesItem[]>([]);
  const favoritesLists = ref<FavoritesList[]>([]);
  const isActivityFeedLoaded = ref(false);
  const product = ref<Product>();
  const publicPromos = ref<PublicPromoCode[]>([]);
  const purchaseHistory = ref<PurchaseHistoryItem[]>([]);
  const purchaseList = ref<StoreFavoritesItem[]>([]);
  const shopProducts = ref<BrandPreviewProduct[]>([]);
  const sponsorships = ref<SponsorshipWithSegmentTrackingData[]>([]);

  const getActiveFavoritesList = computed(() => (userActiveCompanyId: number) => {
    return favoritesLists.value.find((list) => list.buyerId === userActiveCompanyId);
  });

  const getIsCampaignLive = computed(() => {
    return activeCampaign.value?.status === STATUSES.CAMPAIGN.LIVE;
  });

  const getOrganizedDeals = computed(() => {
    let organizedDeals: Deal[] = [];
    const childGroups: object = {};

    deals.value.forEach((deal) => {
      const { parent } = deal;

      if (parent) {
        if (!childGroups[parent.id]) {
          childGroups[parent.id] = [];
        }

        childGroups[parent.id].push(deal);
      } else {
        organizedDeals.push(deal);
      }
    });

    Object.keys(childGroups).forEach((parentId) => {
      const children = childGroups[parentId];

      if (children.length === 1) {
        organizedDeals.push(children[0]);
      } else {
        const parent = Object.assign({}, children[0]);

        parent.children = children;

        children.forEach((child) => {
          if (parent.discount < child.discount) {
            parent.discount = child.discount;
          }
        });

        organizedDeals.push(parent);
      }
    });

    // Default sort discount hight to low
    organizedDeals = organizedDeals.sort((dealA, dealB) => {
      return dealB.discount - dealA.discount;
    });

    return organizedDeals;
  });

  const getPurchaseHistoryByProductId = computed(() => {
    return purchaseHistory.value.reduce((groupedList, item) => {
      if (!groupedList[item.product.id]) {
        groupedList[item.product.id] = [];
      }

      groupedList[item.product.id].push(item);

      return groupedList;
    }, {} as { [product_id: number]: PurchaseHistoryItem[] });
  });

  const getPurchaseListByProductId = computed(() => {
    return purchaseList.value.reduce((groupedList, item) => {
      if (!groupedList[item.product.id]) {
        groupedList[item.product.id] = [];
      }

      groupedList[item.product.id].push(item);

      return groupedList;
    }, {} as { [product_id: number]: StoreFavoritesItem[] });
  });

  const getSponsorshipsByPosition = computed(() => (positionId: string) => {
    const filteredAds = sponsorships.value.filter((sponsorship) => sponsorship.positionId.startsWith(positionId));

    return filteredAds;
  });

  return {
    activeCampaign,
    brandActivities,
    brandMenus,
    newBrands,
    brands,
    deals,
    favoritesItems,
    favoritesLists,
    isActivityFeedLoaded,
    product,
    publicPromos,
    purchaseHistory,
    purchaseList,
    shopProducts,
    sponsorships,
    getActiveFavoritesList,
    getIsCampaignLive,
    getOrganizedDeals,
    getPurchaseHistoryByProductId,
    getPurchaseListByProductId,
    getSponsorshipsByPosition,
  };
};

export type ShopState = ReturnType<typeof setupShopState>;
